import { TConfigUI } from 'src/types/followup'
import { EConvocationDateSetBy, TPatientMedEvent } from 'src/types/med-event'
import { TCompletedMedEvent } from './generic-followup-form.type'
import { PatientService } from 'src/services/patient.service'
import { TPatient } from 'src/types/patient'
import { TDeprecatedPatientVariable } from 'src/types/variable'

export const getValidationErrors = (completedMedEvents: TCompletedMedEvent[], configUi: TConfigUI) => {
  const validationRules = configUi.validationRules ?? []
  return validationRules?.filter((validationRule) => {
    const completedMedEventForThisRule = completedMedEvents.filter(
      (completedMedEvent) => completedMedEvent.medEventUI?.MedEvent.type === validationRule.type,
    )
    // we only check min validation because max validation is check on every call at canAddMedEventToPatientFollowup
    if (!validationRule?.min) {
      return false
    }
    const isNotRespectingMinRule = completedMedEventForThisRule.length < validationRule.min
    return isNotRespectingMinRule
  })
}

export const isCompletedMedEventsIdentical = (
  medEvent1: TCompletedMedEvent,
  medEvent2: TCompletedMedEvent,
) => {
  return (
    medEvent1.LocationId === medEvent2.LocationId &&
    medEvent1.UserId === medEvent2.UserId &&
    medEvent1.date === medEvent2.date &&
    medEvent1.convocationDate === medEvent2.convocationDate &&
    medEvent1.medEventUI?.MedEvent.id === medEvent2.medEventUI?.MedEvent.id &&
    medEvent1.patientMedEventId === medEvent2.patientMedEventId
  )
}

const getOnlyUpdatedProps = ({
  completedMedEvent,
  oldCorrespondingPatientMedEvent,
}: {
  completedMedEvent: TCompletedMedEvent
  oldCorrespondingPatientMedEvent: TPatientMedEvent
}): IFormattedMedEvent | null => {
  const updatedProps = { patientMedEventId: completedMedEvent.patientMedEventId }
  let hasAnUpdatedProp = false
  if (completedMedEvent.LocationId !== oldCorrespondingPatientMedEvent.Location?.id) {
    Object.assign(updatedProps, { LocationId: completedMedEvent.LocationId })
    hasAnUpdatedProp = true
  }
  if (completedMedEvent.UserId !== oldCorrespondingPatientMedEvent.MedicalStaffMember?.id) {
    Object.assign(updatedProps, { MedicalStaffMemberId: completedMedEvent.UserId })
    hasAnUpdatedProp = true
  }
  if (completedMedEvent.medEventUI?.MedEvent.id !== oldCorrespondingPatientMedEvent.MedEventId) {
    Object.assign(updatedProps, { medEventId: completedMedEvent.medEventUI?.MedEvent.id })
    hasAnUpdatedProp = true
  }

  const hasADateChange =
    completedMedEvent.date !== oldCorrespondingPatientMedEvent.date ||
    completedMedEvent.convocationDate !== oldCorrespondingPatientMedEvent.convocationDate
  if (hasADateChange) {
    Object.assign(updatedProps, {
      date: completedMedEvent.date,
      convocationDate: completedMedEvent.convocationDate,
      convocationDateSetBy: completedMedEvent.convocationDateSetBy,
    })
    hasAnUpdatedProp = true
  }

  return hasAnUpdatedProp ? updatedProps : null
}

interface IFormattedMedEvent {
  LocationId?: number
  MedicalStaffMemberId?: number
  date?: Date | string
  convocationDate?: Date | string
  medEventId?: number
  patientMedEventId?: number
}
interface IFollowupPayload {
  MedicalStaffMemberId?: number
  PatientFollowupId?: number
  data: {
    deletedPatientMedEventIds?: number[]
    patientVariables?: { variableName: string; value?: boolean }[]
    addedOrUpdatedPatientMedEvents?: IFormattedMedEvent[]
  }
}
export const saveFollowup = async ({
  patientVariables,
  completedMedEvents,
  deletedPatientMedEventIds,
  patient,
  followupId,
  patientFollowupId,
}: {
  patientVariables: TDeprecatedPatientVariable[]
  completedMedEvents: TCompletedMedEvent[]
  deletedPatientMedEventIds: number[]
  patient: TPatient
  followupId: number
  patientFollowupId?: number
}) => {
  const completedMedEventsWithoutEmptyProps = completedMedEvents.map((completedMedEvent) => {
    delete completedMedEvent.uniqId
    return Object.fromEntries(Object.entries(completedMedEvent).filter(([_, value]) => value != ''))
  }) as TCompletedMedEvent[]

  const addedOrUpdatedPatientMedEvents = completedMedEventsWithoutEmptyProps
    .map((completedMedEvent) => {
      // it's an updated patient medevent
      if (completedMedEvent.patientMedEventId) {
        const oldCorrespondingPatientMedEvent = patient.PatientMedEvents.find(
          (existingPme) => existingPme.id === completedMedEvent.patientMedEventId,
        )
        if (oldCorrespondingPatientMedEvent) {
          return getOnlyUpdatedProps({ completedMedEvent, oldCorrespondingPatientMedEvent })
        }
      }

      // it's an new patient medevent
      return {
        LocationId: completedMedEvent.LocationId,
        MedicalStaffMemberId: completedMedEvent.UserId,
        date: completedMedEvent.date,
        convocationDate: completedMedEvent.convocationDate,
        convocationDateSetBy: completedMedEvent.convocationDateSetBy,
        medEventId: completedMedEvent.medEventUI?.MedEvent.id,
        patientMedEventId: completedMedEvent.patientMedEventId,
      } as IFormattedMedEvent
    })
    .filter(Boolean) as IFormattedMedEvent[]

  const patientVariablesFormatted = patientVariables.map((variable) => ({
    variableName: variable.variableName,
    value: variable.value,
  }))
  const payload: IFollowupPayload = {
    data: {},
    MedicalStaffMemberId:
      patient.MedicalStaffMemberId ||
      addedOrUpdatedPatientMedEvents.find((item: any) => item?.MedicalStaffMemberId)?.MedicalStaffMemberId,
    PatientFollowupId: patientFollowupId,
  }
  if (deletedPatientMedEventIds.length) {
    payload.data.deletedPatientMedEventIds = deletedPatientMedEventIds
  }
  if (addedOrUpdatedPatientMedEvents.length) {
    payload.data.addedOrUpdatedPatientMedEvents = addedOrUpdatedPatientMedEvents
  }
  if (patientVariables.length) {
    payload.data.patientVariables = patientVariablesFormatted
  }
  return await PatientService.updatePatientMedEvents(patient?.ProjectId, patient.id, followupId, payload)
}

export type TMedEventValue = Date | number
export type TMedEventForm = {
  date: Date
  UserId: number
  LocationId: number
  convocationDate: Date
  convocationDateSetBy?: EConvocationDateSetBy | null
}
